import {ShowMore, type ShowMoreRef, type ShowMoreToggleLinesFn} from '@re-dev/react-truncate'
import {announce} from '@react-aria/live-announcer'
import {create, rawReturn} from 'mutative'
import {useMutative} from 'use-mutative'

import BookmarkSvg from '@/assets/icons/bookmark.svg?react'
import cartPlusSvg from '@/assets/icons/cart-plus.svg'
import UndoSvg from '@/assets/icons/undo.svg?react'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import ProductBookmarker from '@/components/ProductCard/ProductBookmarker'
import ProductOriginalPrice from '@/components/ProductCard/ProductOriginalPrice'
import ProductPrice from '@/components/ProductCard/ProductPrice'
import ProductRating from '@/components/ProductCard/ProductRating'
import ProductRetailerImage from '@/components/ProductCard/ProductRetailerImage'
import ProductShippingInfo from '@/components/ProductCard/ProductShippingInfo'
import QuantitySelector from '@/components/QuantitySelector/QuantitySelector'
import {ADD_TO_CART_EVENT} from '@/constants/events'
import useAddToOrRemoveFromCartMutation from '@/lib/cart/hooks/useAddToOrRemoveFromCartMutation'
import {useLocale} from '@/lib/locale/locale'
import {useSetCustomItems} from '@/lib/payment/store'
import preloadProduct from '@/lib/product/utils/preloadProduct'
import type {FullProduct, Product} from '@/models/productModel'
import {PaymentRoute} from '@/routeRegistry'
import type {ProductId} from '@/routes/product/$productId'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import useIdleTimeScheduler from '@/utils/hooks/useIdleTimeScheduler'
import {logEvent} from '@/utils/logger'
import style from '@/views/Product/Product.module.scss'
import {getSelectedVariantFromSelectedOptions} from '@/views/Product/utils'

import ProductVariations from './ProductVariations'

const PriceHistory = lazy(
  async () => import('@/components/ChatSection/componnents/PriceHistory/PriceHistory'),
)

interface Props {
  product: FullProduct
  setProductId: MemoizedCallbackOrDispatch<ProductId>
  isLoading?: boolean
  unavailableProductIds: Set<string>
  markProductIdsAsUnavailable: MemoizedCallbackOrDispatch<ProductId[]>
  refetchProduct?: MemoizedCallback<() => void>
}
export default memo(function ProductInfoAndOptions({
  product,
  setProductId,
  isLoading,
  unavailableProductIds,
  markProductIdsAsUnavailable,
  refetchProduct,
}: Props) {
  const latestProduct = useLatest(product)
  const latestIsLoading = useLatest(isLoading)
  const latestRefetchProduct = useLatest(refetchProduct)
  const queryClient = useQueryClient()
  const [locale] = useLocale()

  const scheduler = useIdleTimeScheduler()
  const [quantity, setQuantity] = useState<number>(1)
  const latestQuantity = useLatest(quantity)

  const preSelectedVariation = useMemo(
    () => product.variations.find(variation => variation.id === product.id),
    [product.id, product.variations],
  )

  const [selectedOptions, setSelectedOptions] = useMutative<Map<string, string>>(
    new Map(preSelectedVariation?.values.entries()),
  )
  const latestSelectedOptions = useLatest(selectedOptions)

  // NOTE: heavy component, shouldnt reset via key
  useEffect(() => {
    if (preSelectedVariation) {
      const newSelectedOptions = new Map(preSelectedVariation.values.entries())
      if (newSelectedOptions.size > 0) {
        setSelectedOptions(() => rawReturn(newSelectedOptions))
      }
    }
  }, [preSelectedVariation, unavailableProductIds])

  const getAvailableOptionValues = useCallback(
    (optionName: string, selectedOptions: Map<string, string>) => {
      const newSelectedOptions = create(selectedOptions, draft => {
        draft.delete(optionName)
      })

      const entries = Array.from(newSelectedOptions.entries())

      const availableVariations = product.variations.filter(variation => {
        for (const [name, value] of entries) {
          const shouldInclude = variation.values.get(name) === value && !variation.outOfStock

          if (!shouldInclude) {
            return false
          }
        }

        return true
      })

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- its guranteed to be exist
      return new Set(availableVariations.map(variation => variation.values.get(optionName)!))
    },
    [product.variations],
  )

  const options = useMemo(() => {
    const options = new Map<string, Map<string, boolean>>()

    product.variations.forEach(variation => {
      variation.values.forEach((value, name) => {
        if (!options.has(name)) {
          options.set(name, new Map([[value, false]]))
        } else {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- its guranteed to be exist
          options.get(name)!.set(value, false)
        }
      })
    })

    options.forEach((value, name) => {
      getAvailableOptionValues(name, selectedOptions).forEach(v => {
        value.set(v, true)
      })

      const sortedOptionValues = new Map([...value.entries()].sort())
      options.set(name, sortedOptionValues)
    })

    return new Map([...options.entries()].sort())
  }, [product.variations, selectedOptions, getAvailableOptionValues])

  // // TODO: Correct selected options; there are some case that after product update, the variations changed, and the selected options is not correct
  // setSelectedOptions(selectedOptions => {
  //   selectedOptions.forEach((value, name) => {
  //     if (!options.get(name)?.has(value)) selectedOptions.delete(name)
  //   })
  //   return selectedOptions
  // })

  const isAllVariationsSelected = Array.from(options).every(option => {
    return selectedOptions.get(option[0])
  })
  const latestIsAllVariationsSelected = useLatest(isAllVariationsSelected)

  const preload = useCallback(
    (product: Pick<Product, 'retailer' | 'id'>) => {
      scheduler.schedule(() => {
        void preloadProduct(product, locale, queryClient).then(result => {
          if (result === false || result.products.length === 0 || result.products[0]?.outOfStock) {
            markProductIdsAsUnavailable([product])
          }
        })
      })
    },
    [markProductIdsAsUnavailable, queryClient, locale],
  )

  //----------------------------------------------------------------------------

  const {mutate: addToOrRemoveFromCart, status: addToOrRemoveFromCartStatus} =
    useAddToOrRemoveFromCartMutation()
  const latestaddToOrRemoveFromCartStatus = useLatest(addToOrRemoveFromCartStatus)

  const onAddProduct = useCallback(() => {
    if (!latestIsAllVariationsSelected.current) return
    if (latestIsLoading.current) return
    if (latestaddToOrRemoveFromCartStatus.current === 'pending') return

    logEvent(...ADD_TO_CART_EVENT)

    const price = latestProduct.current.price
    if (typeof price !== 'number' || Number.isNaN(price)) {
      latestRefetchProduct.current?.()
      toastErrorMessage()
      return
    }

    announce('Adding the product to the cart', 'assertive')

    const variation = Object.fromEntries(latestSelectedOptions.current.entries())

    addToOrRemoveFromCart({
      items: [
        {
          ...latestProduct.current,
          price,
          quantity: latestQuantity.current,
          variation,
        },
      ],
    })
  }, [addToOrRemoveFromCart])

  //----------------------------------------------------------------------------

  const navigate = useNavigate()
  const setCustomItems = useSetCustomItems()

  const [isBuying, setIsBuying] = useState(false)
  const latestIsBuying = useLatest(isBuying)

  const onBuyNow = useCallback(async () => {
    logEvent(...ADD_TO_CART_EVENT)
    if (latestIsBuying.current) return
    if (latestIsLoading.current) return

    const price = latestProduct.current.price
    if (typeof price !== 'number' || Number.isNaN(price)) {
      latestRefetchProduct.current?.()
      toastErrorMessage()
      return
    }

    const variation = Object.fromEntries(latestSelectedOptions.current.entries())

    setIsBuying(true)

    setCustomItems([
      {
        ...latestProduct.current,
        price,
        quantity: latestQuantity.current,
        variation,
      },
    ])

    setIsBuying(false)

    await navigate({
      to: PaymentRoute.fullPath,
      search: {
        custom: true,
      },
    })
  }, [navigate, setCustomItems])

  //----------------------------------------------------------------------------

  ;(function correctProductId() {
    if (selectedOptions.size > 0) {
      const variant = getSelectedVariantFromSelectedOptions(product.variations, selectedOptions)
      if (variant) {
        if (variant.id !== product.id) {
          console.log('Correct id to', variant.id)
          setProductId({
            retailer: product.retailer,
            id: variant.id,
          })
        }
      } else {
        console.warn('No available variation')
      }
    }
  })()

  // useEffect(
  //   function preloadAllVariations() {
  //     const variations = klona(product.variations)

  //     const timer = window.setInterval(() => {
  //       const variation = variations.pop()
  //       if (variation)
  //         preload({
  //           retailer: product.retailer,
  //           id: variation.id,
  //         })
  //     }, 5000)

  //     return () => {
  //       window.clearInterval(timer)
  //     }
  //   },
  //   [preload, product.variations, product.retailer],
  // )

  //----------------------------------------------------------------------------

  const descriptionElement = useRef<HTMLDivElement>(null)

  const ref = useRef<ShowMoreRef>(null)

  const toggleLines: ShowMoreToggleLinesFn = e => {
    ref.current?.toggleLines(e)
  }

  return (
    <div className={clsx(style.ProductInfoAndOptions, isLoading && style.IsLoading)}>
      <div className={style.ProductInformation}>
        <ProductRetailerImage retailer={product.retailer} seller={product.seller} />
        <h1 className={style.ProductName}>{product.title}</h1>
        <div>
          <ProductRating rating={product.rating} ratingCount={product.ratingCount} />
        </div>
        <div className={style.Prices}>
          <ProductPrice price={product.price} />
          <Suspense>
            <PriceHistory id={product.id} title={product.title} retailer={product.retailer} />
          </Suspense>
        </div>
        {!!product.orgPrice && (
          <div>
            <ProductOriginalPrice orgPrice={product.orgPrice} price={product.price} />
          </div>
        )}
        {!!product.description && (
          <>
            <VisuallyHidden as='h2'>Product description</VisuallyHidden>
            <div ref={descriptionElement} className={clsx(style.Description)}>
              <ShowMore
                ref={ref}
                lines={5}
                more={
                  <button onClick={toggleLines} className={style.ReadMore}>
                    Read more
                  </button>
                }
                less={
                  <button onClick={toggleLines} className={style.ReadMore}>
                    Read less
                  </button>
                }
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: product.description,
                  }}
                />
              </ShowMore>
            </div>
          </>
        )}
      </div>
      <hr className={style.Divider} aria-hidden />
      <div className={style.AdditionInfo}>
        <VisuallyHidden as='h2'>Shipping and return</VisuallyHidden>
        <ProductShippingInfo value={product.shipping} />
        {product.return && (
          <div className={style.ReturnPolicy}>
            <UndoSvg aria-hidden />
            {product.return}
          </div>
        )}
      </div>
      <hr className={style.Divider} aria-hidden />
      <div className={style.Options}>
        <table className={style.Variations} role='none'>
          <tbody role='none'>
            <ProductVariations
              options={options}
              selectedOptions={selectedOptions}
              retailer={product.retailer}
              variations={product.variations}
              setSelectedOptions={setSelectedOptions}
              unavailableProductIds={unavailableProductIds}
              preload={preload}
            />
            {!product.outOfStock && (
              <>
                {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- we have fieldset */}
                <tr className={style.Variation} role='none'>
                  <td className={style.VariationName} aria-hidden>
                    Quantity
                  </td>
                  {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- we have fieldset */}
                  <td className={style.VariationValues} role='none'>
                    <QuantitySelector
                      quantity={quantity}
                      setQuantity={setQuantity}
                      maxQuantity={product.availableQuantity}
                    />
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className={style.Actions}>
        {product.outOfStock ? (
          <button className={style.AddToCart} aria-disabled>
            Out of stock
          </button>
        ) : (
          <>
            <button
              className={style.AddToCart}
              aria-disabled={
                !isAllVariationsSelected || addToOrRemoveFromCartStatus === 'pending' || isLoading
              }
              onClick={onAddProduct}
            >
              <img className={style.CartIcon} src={cartPlusSvg} alt='' />
              {addToOrRemoveFromCartStatus !== 'pending' ? 'Add to cart' : 'Adding...'}
            </button>
            <PrimaryButton
              className={style.BuyNow}
              isDisabled={!isAllVariationsSelected}
              isPending={isBuying}
              onPress={onBuyNow}
            >
              {isBuying ? 'Buying...' : 'Buy now'}
            </PrimaryButton>
          </>
        )}
      </div>
      <ProductBookmarker
        product={product}
        className={style.Bookmark}
        activeClassName={style.IsActive}
      >
        <BookmarkSvg
          className={style.BookmarkIcon}
          width={16}
          height={16}
          viewBox='0 0 24 24'
          aria-hidden
        />
      </ProductBookmarker>
    </div>
  )
})
